const Storage = require('utils/storage');
const GetQueryParams = require('utils/get-query-params');
const SsoUtils = require('utils/sso');
const RoutingTable = require('services/auth-management/routing-table');
const AuthConstants = require('services/auth-management/constants');
const AuthPersistence = require('services/auth-management/persistence');
const AuthDebug = require('services/auth-management/debug');
const Policy = require('services/auth-management/policy');

const SIGNUP_QUERY_PARAMS = [
    'v',
    'email',
    's',
    'r',
    'sso',
    'flow',
    'redirect',
    'target',
    'focus'
];

exports.isChoosePasswordRoute = (location) => {

    const pathname = location?.pathname || window.location.pathname;

    return pathname === AuthConstants.authFlow.routes.signup.password;
};

exports.hasInviteEmailContext = (location) => {

    const {
        v
    } = GetQueryParams(location);

    return exports.isChoosePasswordRoute(location)
        && typeof v === 'string'
        && !!v.trim();
};

exports.hasWelcomeEmailContext = exports.hasInviteEmailContext;

exports.hasSignupQueryParams = (location) => {

    const query = GetQueryParams(location);

    return SIGNUP_QUERY_PARAMS.some((key) => Object.prototype.hasOwnProperty.call(query, key));
};

exports.consumeSignupQueryParams = () => {

    if (typeof window === 'undefined') {
        return false;
    }

    const currentUrl = new URL(window.location.href);
    const hasSignupQueryParams = SIGNUP_QUERY_PARAMS.some((key) => currentUrl.searchParams.has(key));

    if (!hasSignupQueryParams) {
        return false;
    }

    SIGNUP_QUERY_PARAMS.forEach((key) => currentUrl.searchParams.delete(key));

    const nextSearch = currentUrl.searchParams.toString();
    const nextUrl = `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ''}${currentUrl.hash}`;

    window.history.replaceState(window.history.state, document.title, nextUrl);

    return true;
};

exports.consumeInviteEmailQueryParams = (location) => {

    if (!exports.hasInviteEmailContext(location)) {
        return false;
    }

    return exports.consumeSignupQueryParams();
};

exports.getSignupContextWithQueryParams = async (context, location, { inviteContext } = {}) => {

    const redux     = context.redux.hooks;
    const actions   = context.actions;
    const selectors = context.selectors.all;

    const routeContext = { ...RoutingTable.getAuthRouteContext(location), ...inviteContext };

    const hasInviteEmailContext = exports.hasInviteEmailContext(location);
    const persistedSignupContext = selectors.getSignupContext(redux.getState()) || {};
    const routeEmail = Policy.normalizeEmail(routeContext.email);
    const persistedEmail = Policy.normalizeEmail(persistedSignupContext.email);
    const hasDifferentRouteIdentity = !!routeEmail
        && !!persistedEmail
        && routeEmail !== persistedEmail;

    if (hasDifferentRouteIdentity) {
        AuthPersistence.clearTransientAuthState();
    }

    const signupContext = hasInviteEmailContext || hasDifferentRouteIdentity
        ? { ...AuthConstants.signupContext.resetFields, ...inviteContext }
        : persistedSignupContext;

    const newSignupContext = {
        ...signupContext,
        verificationToken: routeContext.verificationToken || signupContext.verificationToken,
        schoolId: routeContext.schoolId || signupContext.schoolId,
        roleId: routeContext.roleId || signupContext.roleId,
        email: routeContext.email || signupContext.email,
        sso: routeContext.sso || signupContext.sso,
        redirect: routeContext.redirect || signupContext.redirect,
        redirectTarget: routeContext.target || signupContext.redirectTarget,
        agreeToTerms: signupContext.agreeToTerms === true
    };

    exports.consumeSignupQueryParams();

    await internals.resolveSchoolContext({
        actions,
        location,
        newSignupContext,
        querySSO: routeContext.sso,
        redux,
        selectors
    });

    if (inviteContext) {
        // The token lookup accounts for MFA and internal SSO bypasses. School
        // configuration and legacy URL hints must not replace that decision.
        newSignupContext.sso = inviteContext.sso;
    }

    newSignupContext.sso = SsoUtils.normalizeSSO(newSignupContext.sso);
    const authFlow = RoutingTable.resolveAuthFlow({
        currentContext: signupContext,
        email: newSignupContext.email,
        preserveCurrentMfa: true,
        response: newSignupContext,
        source: AuthConstants.authFlow.sources.signupRouteContext
    });

    Object.assign(
        newSignupContext,
        RoutingTable.buildAuthFlowContextUpdate({ authFlow })
    );

    if (newSignupContext.authMethod !== AuthConstants.authFlow.methods.sso) {
        newSignupContext.agreeToTerms = newSignupContext.agreeToTerms
            || Storage.get(AuthConstants.termsAgreement.storageKey) === true;
    }

    AuthDebug.log('signup_context_resolved_from_route', {
        location,
        query: GetQueryParams(location),
        signupContext: newSignupContext
    });

    return newSignupContext;
};

exports.getSignupVars = (type) => {

    AuthPersistence.migrateAuthPersistence();

    const persistedSignupContext = Storage.get(AuthConstants.signupContext.storageKey) || {};

    if (type === 'context') {
        return {
            ...persistedSignupContext || {},
            fetchedProgress: false
        };
    }
    else if (type === 'step1') {
        const {
            email,
            schoolId
        } = persistedSignupContext || {};

        return {
            email,
            schoolId
        };
    }
    else if (type === 'step2') {
        const {
            verificationToken
        } = persistedSignupContext || {};

        return {
            verificationToken
        };
    }
};

const internals = {};

internals.resolveSchoolContext = async ({
    actions,
    location,
    newSignupContext,
    querySSO,
    redux,
    selectors
}) => {

    let school = null;
    const {
        schoolId,
        verificationToken
    } = newSignupContext;
    const hasSchoolDetails = schoolId
        ? !!selectors.getSchoolExtensionById(redux.getState(), schoolId)
        : false;
    const isAuthenticated = selectors.getIsAuthenticated(redux.getState());
    const shouldUseVerificationToken = !!verificationToken && !hasSchoolDetails && !isAuthenticated;
    const res = await RoutingTable.ensureSchoolDetails({
        actions,
        redux,
        schoolId,
        selectors,
        verificationToken
    });

    if (shouldUseVerificationToken) {
        const newSchoolId = res?.payload?.result?.result?.school;
        school = newSchoolId
            ? selectors.getSchoolById(redux.getState(), newSchoolId)
            : null;

        if (!newSchoolId || !school) {
            const error = new Error('[signup-context] token school lookup did not return a school');
            error.code = 'SIGNUP_CONTEXT_SCHOOL_LOOKUP_FAILED';
            error.details = {
                location,
                query: GetQueryParams(location),
                response: res,
                signupContext: newSignupContext
            };

            console.error(error.message, error.details);
            throw error;
        }

        newSignupContext.schoolId = newSchoolId;
    }
    else if (schoolId) {
        school = selectors.getSchoolById(redux.getState(), schoolId);
    }

    newSignupContext.sso = SsoUtils.normalizeSSO(school?.sso || querySSO || newSignupContext.sso);
};
